import { Head, Link, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { sessionPath, settingsSessionsPath } from "@/routes"
import type { BreadcrumbItem, Session } from "@/types"

interface SessionsProps {
  sessions: Session[]
}

export default function Sessions({ sessions }: SessionsProps) {
  const { t } = useTranslation()
  const { auth } = usePage().props

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("settings.sessions.title"),
      href: settingsSessionsPath(),
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title={t("settings.sessions.heading")}
            description={t("settings.sessions.description")}
          />

          <div className="space-y-4">
            {sessions.map((session) => (
              <div
                key={session.id}
                className="flex flex-col space-y-2 rounded-lg border p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">
                      {session.user_agent}
                      {session.id === auth.session.id && (
                        <Badge variant="secondary" className="ml-2">
                          {t("settings.sessions.current")}
                        </Badge>
                      )}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {t("settings.sessions.ip")}: {session.ip_address}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {t("settings.sessions.active_since")}:{" "}
                      {new Date(session.created_at).toLocaleString()}
                    </p>
                  </div>
                  {session.id !== auth.session.id && (
                    <Button variant="destructive" asChild>
                      <Link
                        method="delete"
                        href={sessionPath({ id: session.id })}
                        as="button"
                      >
                        {t("settings.sessions.logout")}
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
